import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  FiSearch, 
  FiFilter, 
  FiDownload, 
  FiUpload, 
  FiTrash2,
  FiCopy,
  FiShare2,
  FiCode,
  FiCalendar,
  FiClock,
  FiEye,
  FiStar,
  FiChevronDown,
  FiPlus,
  FiFolder,
  FiFile
} from 'react-icons/fi'
import { 
  IoLanguageOutline,
  IoStatsChartOutline,
  IoCloudDownloadOutline
} from 'react-icons/io5'
import { MdOutlineDataUsage, MdOutlineSort } from 'react-icons/md'
import { TbBraces, TbFileCode } from 'react-icons/tb'
import { FaFire } from 'react-icons/fa'
import toast, { Toaster } from 'react-hot-toast'
import { localStorageUtils } from '../hooks/useLocalStorage'

const Explore = () => {
  const navigate = useNavigate()
  const [files, setFiles] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterLanguage, setFilterLanguage] = useState('all')
  const [sortBy, setSortBy] = useState('updatedAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isDark, setIsDark] = useState(false)
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false)

  const languages = [
    { value: 'all', label: 'All Languages', color: 'bg-gray-500' },
    { value: 'javascript', label: 'JavaScript', color: 'bg-yellow-500' },
    { value: 'python', label: 'Python', color: 'bg-blue-500' },
    { value: 'html', label: 'HTML', color: 'bg-red-500' },
    { value: 'css', label: 'CSS', color: 'bg-pink-500' },
    { value: 'java', label: 'Java', color: 'bg-orange-500' },
    { value: 'cpp', label: 'C++', color: 'bg-purple-500' },
    { value: 'typescript', label: 'TypeScript', color: 'bg-blue-600' },
    { value: 'php', label: 'PHP', color: 'bg-indigo-500' },
    { value: 'json', label: 'JSON', color: 'bg-green-500' },
  ]

  useEffect(() => {
    loadFiles()
    const isDarkMode = localStorage.getItem('theme') === 'dark' || 
      window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(isDarkMode)
  }, [])

  const loadFiles = () => {
    try {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      const filesArray = Object.values(storedFiles)
      setFiles(filesArray)
    } catch (error) {
      console.error('Error loading files:', error)
      setFiles([])
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    toast.success(`Searching for: ${searchQuery}`)
  }

  const handleFileSelect = (fileId) => {
    navigate(`/editor/${fileId}`)
  }

  const handleDelete = (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      delete storedFiles[fileId]
      localStorageUtils.setItem('codeFiles', storedFiles)
      loadFiles()
      toast.success('File deleted successfully! 🗑️')
    }
  }

  const handleDeleteSelected = () => {
    if (selectedFiles.length === 0) {
      toast.error('No files selected')
      return
    }
    
    if (window.confirm(`Delete ${selectedFiles.length} selected files?`)) {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      selectedFiles.forEach(id => {
        delete storedFiles[id]
      })
      localStorageUtils.setItem('codeFiles', storedFiles)
      setSelectedFiles([])
      loadFiles()
      toast.success(`${selectedFiles.length} files deleted! 🗑️`)
    }
  }

  const handleShare = (fileId) => {
    const url = `${window.location.origin}/editor/${fileId}`
    navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard! 🔗')
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = e.target.files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result)
          const storedFiles = localStorageUtils.getItem('codeFiles', {})
          
          Object.keys(importedData).forEach(key => {
            storedFiles[key] = importedData[key]
          })
          
          localStorageUtils.setItem('codeFiles', storedFiles)
          loadFiles()
          toast.success('Files imported successfully! 📤')
        } catch (error) {
          toast.error('Invalid file format')
        }
      }
      reader.readAsText(file)
    }
    input.click()
  }

  const handleExportAll = () => {
    const storedFiles = localStorageUtils.getItem('codeFiles', {})
    const dataStr = JSON.stringify(storedFiles, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    
    const exportFileDefaultName = 'codefire-files.json'
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', exportFileDefaultName)
    linkElement.click()
    
    toast.success('All files exported! 📥')
  }

  const toggleSelectFile = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    )
  }

  const selectAllFiles = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([])
    } else {
      setSelectedFiles(filteredFiles.map(f => f.id))
    }
  }

  const filteredFiles = files
    .filter(file => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        return (
          file.name.toLowerCase().includes(query) ||
          file.content?.toLowerCase().includes(query)
        )
      }
      return true
    })
    .filter(file => filterLanguage === 'all' || file.language === filterLanguage)
    .sort((a, b) => {
      const order = sortOrder === 'asc' ? 1 : -1
      
      switch (sortBy) {
        case 'name':
          return order * a.name.localeCompare(b.name)
        case 'createdAt':
          return order * (new Date(a.createdAt) - new Date(b.createdAt))
        case 'updatedAt':
          return order * (new Date(a.updatedAt) - new Date(b.updatedAt))
        case 'size':
          return order * ((a.content?.length || 0) - (b.content?.length || 0))
        default:
          return 0
      }
    })

  const getFileStats = () => {
    const totalLines = files.reduce((sum, file) => sum + (file.content?.split('\n').length || 0), 0)
    const totalSize = files.reduce((sum, file) => sum + (file.content?.length || 0), 0)
    
    return {
      totalFiles: files.length,
      totalLines,
      totalSize: (totalSize / 1024).toFixed(2),
      languages: [...new Set(files.map(f => f.language))].filter(Boolean).length
    }
  }

  const stats = getFileStats()

  return (
    <div className="min-h-screen pt-20 pb-8 bg-gradient-to-br from-primary-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30 transition-colors duration-300">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            borderRadius: '12px',
            fontSize: '14px',
          },
        }}
      />
      
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-2">
                <span className="bg-gradient-to-r from-primary-600 to-red-500 bg-clip-text text-transparent">
                  Explore
                </span> Files
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
                Browse and manage all your CodeFire projects
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Link to="/editor">
                <button className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white hover:from-primary-600 hover:to-red-600 shadow-lg">
                  <FiPlus />
                  <span>New File</span>
                </button>
              </Link>
              <button
                onClick={handleImport}
                className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <FiUpload />
                <span>Import</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {[
            { label: 'Total Files', value: stats.totalFiles, icon: <TbFileCode />, color: 'from-blue-500 to-cyan-500' },
            { label: 'Total Lines', value: stats.totalLines.toLocaleString(), icon: <TbBraces />, color: 'from-green-500 to-emerald-500' },
            { label: 'Storage Used', value: `${stats.totalSize} KB`, icon: <MdOutlineDataUsage />, color: 'from-purple-500 to-pink-500' },
            { label: 'Languages', value: stats.languages, icon: <IoLanguageOutline />, color: 'from-orange-500 to-red-500' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <div className="text-white text-xl">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8 shadow-lg"
        >
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <form onSubmit={handleSearch} className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files by name or content..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none transition-all text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </form>
            </div>

            {/* Filter and Sort */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <button 
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                  className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
                >
                  <FiFilter />
                  <span>Filter</span>
                  <FiChevronDown />
                </button>
                
                {showLanguageDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl z-50 p-2">
                    {languages.map((lang) => (
                      <button
                        key={lang.value}
                        onClick={() => {
                          setFilterLanguage(lang.value)
                          setShowLanguageDropdown(false)
                        }}
                        className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          filterLanguage === lang.value ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}
                      >
                        <div className={`w-3 h-3 rounded-full ${lang.color}`}></div>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleExportAll}
                className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-500 to-red-500 text-white shadow-sm"
              >
                <IoCloudDownloadOutline />
                <span>Export All</span>
              </button>
            </div>
          </div>

          {/* Language Filter Chips */}
          <div className="flex flex-wrap gap-2 mt-6">
            {languages.slice(0, 6).map((lang) => (
              <button
                key={lang.value}
                onClick={() => setFilterLanguage(lang.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filterLanguage === lang.value
                    ? `${lang.color} text-white`
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Files Grid */}
        {filteredFiles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-lg"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-700 dark:to-primary-900/20 flex items-center justify-center">
              <FaFire className="text-4xl text-primary-600 dark:text-primary-400 animate-pulse" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              No files found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? `No files match "${searchQuery}". Try a different search term.`
                : 'Create your first code file to get started!'}
            </p>
            <Link to="/editor">
              <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-red-500 text-white hover:from-primary-700 hover:to-red-600 shadow-lg">
                Create First File
              </button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Files Header */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-gray-700 dark:text-gray-300">
                Showing <span className="font-bold text-primary-600 dark:text-primary-400">{filteredFiles.length}</span> of{' '}
                <span className="font-bold">{files.length}</span> files
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={selectAllFiles}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {selectedFiles.length === filteredFiles.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedFiles.length > 0 && (
                  <button
                    onClick={handleDeleteSelected}
                    className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400"
                  >
                    <FiTrash2 />
                    <span>Delete Selected ({selectedFiles.length})</span>
                  </button>
                )}
              </div>
            </div>

            {/* Files Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFiles.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  onClick={() => handleFileSelect(file.id)}
                  className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 dark:border-gray-700 cursor-pointer"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.id)}
                          onChange={() => toggleSelectFile(file.id)}
                          className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white truncate max-w-[180px]">
                          {file.name}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${languages.find(l => l.value === file.language)?.color || 'bg-gray-500'} text-white`}>
                            {file.language || 'text'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {file.content?.split('\n').length || 0} lines
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleShare(file.id)
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Share"
                      >
                        <FiShare2 className="text-green-500" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(file.id)
                        }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Delete"
                      >
                        <FiTrash2 className="text-red-500" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 font-mono">
                    {file.content?.substring(0, 100) || 'No content'}...
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <FiCalendar />
                        <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <FiClock />
                        <span>{new Date(file.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FiEye />
                      <span>{(file.content?.length || 0).toLocaleString()} chars</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Storage Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary-50 to-red-50 dark:from-gray-800 dark:to-primary-900/30 rounded-2xl p-8 shadow-lg border border-primary-200 dark:border-primary-800/30"
        >
          <div className="flex items-center space-x-4 mb-6">
            <IoStatsChartOutline className="text-3xl text-primary-600 dark:text-primary-400" />
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Storage Information</h3>
              <p className="text-gray-600 dark:text-gray-400">
                All your code is stored locally in your browser
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Storage Type', value: 'LocalStorage', icon: '💾' },
              { label: 'Total Capacity', value: '~5 MB', icon: '📊' },
              { label: 'Files Supported', value: 'Unlimited', icon: '📁' },
              { label: 'Lines Per File', value: '10,000+', icon: '📏' },
            ].map((item, index) => (
              <div
                key={item.label}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{item.icon}</span>
                  <div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">{item.label}</div>
                    <div className="font-bold text-gray-900 dark:text-white">{item.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-white dark:bg-gray-800 rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Storage Usage</span>
              <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                {stats.totalSize} KB / 5 MB
              </span>
            </div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary-500 to-red-500"
                style={{ width: `${Math.min((parseFloat(stats.totalSize) / 5120) * 100, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
              {Math.round((parseFloat(stats.totalSize) / 5120) * 100)}% of local storage used
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Explore